import React, { useState, useRef, useEffect, forwardRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getProxyUrl } from "../../utils/proxy";
import BASE_URL from "../../configs/api";
import axios from "axios";

const MovieCard = forwardRef(({ movie }, ref) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPos({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });

    timeoutRef.current = setTimeout(() => {
      setShowPopup(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setShowPopup(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleMouseEnter(e);
    }
    if (e.key === "Escape") {
      setShowPopup(false);
    }
  };

  const handleXemNgay = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      let ngonNguUsed = "vietsub";
      let res = await axios.get(
        `${BASE_URL}/api/video-nguon/phim/${movie.slug}/tap/1/${ngonNguUsed}/`
      );

      if (!res.data?.link_video || !res.data?.so_tap) {
        ngonNguUsed = "thuyetminh";
        res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${movie.slug}/tap/1/${ngonNguUsed}/`
        );
      }

      const tapPhim = res.data;
      if (!tapPhim?.link_video || !tapPhim?.so_tap) return;

      navigate(
        `/xem-phim/${movie.slug}/tap/${tapPhim.so_tap}?id=${tapPhim.id}&ngonngu=${ngonNguUsed}`
      );
    } catch (error) {}
  };

  useEffect(() => {
    const handleScrollClose = () => {
      if (showPopup) setShowPopup(false);
    };

    window.addEventListener("wheel", handleScrollClose, { passive: true });
    window.addEventListener("scroll", handleScrollClose, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleScrollClose);
      window.removeEventListener("scroll", handleScrollClose);
    };
  }, [showPopup]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.ten_phim,
    alternateName: movie.ten_khac,
    image: movie.banner_url,
    datePublished: movie.nam_phat_hanh,
    duration: movie.thoi_luong,
    genre: movie.the_loai?.map((genre) => genre.ten) || [],
    inLanguage: movie.ngon_ngu,
    url: `/phim/${movie.slug}`,
  };

  return (
    <article
      className="relative flex-shrink-0 group cursor-pointer w-1/2 sm:w-1/3 lg:basis-1/3 xl:basis-[24%]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="article"
      aria-label={`Phim ${movie.ten_phim}`}
      ref={ref}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Link
        to={`/phim/${movie.slug}`}
        onClick={() => setShowPopup(false)}
        aria-label={`Xem chi tiết phim ${movie.ten_phim}`}
      >
        <figure className="aspect-[16/9] w-full rounded-md overflow-hidden shadow-md animate-fadeIn transition transform">
          <img
            src={movie.banner_url}
            alt={`Poster phim ${movie.ten_phim} - ${
              movie.ten_khac || "Phim mới"
            }`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://placehold.co/400x225/2d3748/e2e8f0?text=No+Image";
            }}
          />

          {/* Movie metadata badges */}
          <figcaption className="absolute bottom-1 lg:bottom-0 left-2 lg:left-5 flex flex-col lg:flex-row gap-1 xxl:gap-0 justify-center items-start z-20">
            <span
              className="bg-gradient-to-r from-gray-700 to-gray-600 text-white text-[8px] leading-[0.5rem] md:text-xs font-semibold px-2 py-1 rounded lg:rounded-none lg:rounded-tl-[4px] border-r-lime-500"
              aria-label={`Ngôn ngữ: ${movie.ngon_ngu || "Đang cập nhật"}`}
            >
              {movie.ngon_ngu
                ? movie.ngon_ngu
                    .replace("Vietsub", "P.Đề")
                    .replace("Lồng Tiếng", "T.M")
                    .replace("Thuyết Minh", "T.M")
                : "Đang cập nhật"}
            </span>
            <span
              className="bg-gradient-to-r from-[#C02425] to-[#fd9a05] text-white text-[8px] leading-[0.5rem] md:text-xs font-semibold px-2 py-1 rounded lg:rounded-none lg:rounded-tr-[4px]"
              aria-label={`Tình trạng: ${movie.tinh_trang || "Đang cập nhật"}`}
            >
              {(movie.tinh_trang || "Đang cập nhật")
                .replace(/Hoàn Tất/gi, "Full")
                .trim() || "Đang cập nhật"}
            </span>
          </figcaption>
        </figure>
      </Link>

      {/* Enhanced popup with better accessibility */}
      <aside
        className={`hidden xl:block fixed z-50 w-[360px] bg-gradient-to-br from-slate-900 to-slate-800 backdrop-blur-xl rounded-lg shadow-2xl border border-white/10 transition-all duration-500 ease-out ${
          showPopup
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 translate-y-4"
        }`}
        style={{
          top: popupPos.y - 100,
          left: popupPos.x - 180,
          pointerEvents: showPopup ? "auto" : "none",
        }}
        role="dialog"
        aria-labelledby={`movie-title-${movie.slug}`}
        aria-describedby={`movie-desc-${movie.slug}`}
        aria-hidden={!showPopup}
      >
        {/* Movie banner with enhanced effects */}
        <header className="relative w-full h-[200px] rounded-t-lg overflow-hidden">
          <img
            src={movie.banner_url}
            alt={`Banner phim ${movie.ten_phim}`}
            className="w-full h-full object-cover transform transition-transform duration-700"
            loading="lazy"
            decoding="async"
            style={{
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 10%, rgba(0,0,0,0))",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 10%, rgba(0,0,0,0))",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        </header>

        {/* Content section */}
        <div className="p-6 space-y-4">
          {/* Title and actions */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <Link to={`/phim/${movie.slug}`}>
                <h3
                  id={`movie-title-${movie.slug}`}
                  className="text-white font-bold text-sm hover:text-yellow-400 transition-colors duration-200 line-clamp-2 leading-tight"
                >
                  {movie.ten_phim}
                </h3>
              </Link>
              <p
                id={`movie-desc-${movie.slug}`}
                className="text-slate-400 text-xs mt-1 line-clamp-1"
              >
                {movie.ten_khac || "Đang cập nhật thông tin"}
              </p>
            </div>

            {/* Action buttons */}
            <div
              className="flex gap-2"
              role="group"
              aria-label="Hành động phim"
            >
              <button
                onClick={handleXemNgay}
                className="p-2.5 rounded-xl font-semibold transition-all duration-200 hover:scale-110 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-500"
                style={{
                  background:
                    "linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff4081 100%)",
                }}
                aria-label={`Xem ngay phim ${movie.ten_phim}`}
                title="Xem ngay"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-white group-hover:scale-110 transition-transform duration-200"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M19.5 14.598c2-1.155 2-4.041 0-5.196l-9-5.196C8.5 3.05 6 4.494 6 6.804v10.392c0 2.31 2.5 3.753 4.5 2.598z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <Link
                to={`/phim/${movie.slug}`}
                className="group bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 text-white p-2.5 rounded-xl font-semibold transition-all duration-200 hover:scale-110 hover:shadow-lg focus:scale-110 focus:outline-none focus:ring-2 focus:ring-slate-500"
                title={`Chi tiết phim ${movie.ten_phim}`}
                aria-label={`Xem chi tiết phim ${movie.ten_phim}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="group-hover:scale-110 transition-transform duration-200"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 10a2 2 0 1 0 2 2a2 2 0 0 0-2-2m-7 0a2 2 0 1 0 2 2a2 2 0 0 0-2-2m14 0a2 2 0 1 0 2 2a2 2 0 0 0-2-2"
                  />
                </svg>
              </Link>
            </div>
          </div>

          {/* Movie metadata */}
          <dl className="flex flex-wrap gap-2">
            {movie.chat_luong && (
              <div className="px-2 py-0.5 rounded-md text-[10px] lg:text-xs font-bold text-white tracking-wide shadow-lg border border-orange-500 bg-transparent">
                <dt className="sr-only">Chất lượng:</dt>
                <dd>{movie.chat_luong}</dd>
              </div>
            )}

            {movie.tinh_trang && (
              <div className="px-1.5 py-0.5 rounded-md text-[10px] lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-black bg-white">
                <dt className="sr-only">Tình trạng:</dt>
                <dd>{movie.tinh_trang}</dd>
              </div>
            )}

            {movie.nam_phat_hanh && (
              <div className="px-2 py-0.5 rounded-md text-[10px] lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent">
                <dt className="sr-only">Năm phát hành:</dt>
                <dd>{movie.nam_phat_hanh}</dd>
              </div>
            )}

            {movie.thoi_luong && (
              <div className="px-2 py-0.5 rounded-md text-[10px] lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent">
                <dt className="sr-only">Thời lượng:</dt>
                <dd>{movie.thoi_luong}</dd>
              </div>
            )}
          </dl>

          {/* Genres */}
          <section className="pt-2 border-t border-slate-700/50">
            <h4 className="sr-only">Thể loại</h4>
            <nav
              className="flex flex-wrap gap-1 text-xs"
              aria-label="Thể loại phim"
            >
              {movie.the_loai && movie.the_loai.length > 0 ? (
                movie.the_loai.slice(0, 4).map((genre, idx) => (
                  <span key={genre.slug} className="flex items-center">
                    {idx !== 0 && (
                      <span
                        className="mx-2 w-1 h-1 bg-slate-500 rounded-full"
                        aria-hidden="true"
                      />
                    )}
                    <Link
                      to={`/the-loai/${genre.slug}`}
                      className="text-slate-300 hover:text-yellow-400 transition-colors duration-200 font-medium focus:outline-none focus:underline"
                      aria-label={`Thể loại ${genre.ten}`}
                    >
                      {genre.ten}
                    </Link>
                  </span>
                ))
              ) : (
                <span className="text-slate-500 italic text-xs">
                  Thể loại đang cập nhật
                </span>
              )}
            </nav>
          </section>
        </div>
      </aside>
    </article>
  );
});

export default MovieCard;
